// src/Components/ImportMarkers/ExcelHistoryView.js

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';

const DARK_BLUE = '#00008B';
const cellStyle = { paddingLeft: '8px', paddingRight: '8px' };

const formatDateOnly = (isoString) => {
  if (!isoString) return 'Unknown Date';
  const d = new Date(isoString);
  return d.toLocaleDateString();
};

const formatTimeOnly = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

export default function ExcelHistoryView({
  importHistory,
  isLoadingHistory,
  selectedHistoryIds,
  setSelectedHistoryIds,
}) {
  const handleCheckboxChange = (id) => {
    setSelectedHistoryIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedHistoryIds(importHistory.map((row) => row.id));
    } else {
      setSelectedHistoryIds([]);
    }
  };

  const rowCount = importHistory.length;
  const numSelected = selectedHistoryIds.length;
  const isAllSelected = rowCount > 0 && numSelected === rowCount;
  const isIndeterminate = numSelected > 0 && numSelected < rowCount;

  if (isLoadingHistory) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 3,
        }}>
        <CircularProgress sx={{ color: '#00B0FF' }} />
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        flexGrow: 1,
        width: '100%',
        boxSizing: 'border-box',
        borderTop: '2px solid #2e7d32',
        borderBottom: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        borderRadius: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollBehavior: 'smooth',
      }}>
      {rowCount === 0 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            p: 3,
            textAlign: 'center',
          }}>
          <Typography color='text.secondary' variant='body2' sx={{ fontStyle: 'italic' }}>
            No import history found.
          </Typography>
        </Box>
      ) : (
        <Table stickyHeader size='small' sx={{ width: '100%' }}>
          <TableHead>
            <TableRow
              sx={{
                height: '28px',
                '& th': {
                  backgroundColor: '#e8f5e9',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #2e7d32',
                  py: '2px',
                  ...cellStyle,
                },
              }}>
              <TableCell padding='checkbox' sx={{ width: '50px', textAlign: 'center' }}>
                <Checkbox
                  color='primary'
                  indeterminate={isIndeterminate}
                  checked={isAllSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  size='small'
                  sx={{ p: 0.5 }}
                />
              </TableCell>
              <TableCell sx={{ width: '20%' }}>Import Date</TableCell>
              <TableCell sx={{ width: '15%' }}>Time</TableCell>
              <TableCell sx={{ width: '35%' }}>Batch Filename</TableCell>
              <TableCell align='center' sx={{ width: '15%' }}>
                Markers
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {importHistory.map((row) => {
              const isSelected = selectedHistoryIds.includes(row.id);
              return (
                <TableRow
                  key={row.id}
                  hover={true}
                  selected={isSelected}
                  sx={{
                    height: '28px',
                    '& td': { py: '2px', ...cellStyle },
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&.Mui-selected': { backgroundColor: 'transparent !important' },
                    '&.Mui-selected:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                    },
                  }}>
                  <TableCell padding='checkbox' sx={{ textAlign: 'center' }}>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleCheckboxChange(row.id)}
                      size='small'
                      sx={{
                        p: 0.5,
                        '&.Mui-checked': { color: '#2e7d32' },
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant='body2'
                      sx={{ color: DARK_BLUE, fontWeight: 'bold' }}>
                      {formatDateOnly(row.importDate)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant='body2'
                      sx={{ color: DARK_BLUE, fontWeight: 'bold' }}>
                      {formatTimeOnly(row.importDate)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      {row.filename || 'Unknown'}
                    </Typography>
                  </TableCell>

                  <TableCell align='center'>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                      {row.markerCount || 0}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
}
